import type { BotTier } from '@qoobd/rules';

/**
 * Challenge links carry a cube seed and the bot's difficulty, so a friend plays the exact same
 * cube against the same bot. A winning result also carries the time and steps to beat.
 *   /?c=482913.r            "try this cube"
 *   /?c=482913.r.742.31     "beat 1:14.2 in 31 steps"  (time in tenths of a second)
 */
export interface Challenge {
  seed: number;
  tier: BotTier;
  /** Time to beat in ms, when the sender won. */
  ms: number | null;
  steps: number | null;
}

const CODE: Record<BotTier, string> = { novice: 'n', tactical: 't', relentless: 'r' };
const TIER: Record<string, BotTier> = { n: 'novice', t: 'tactical', r: 'relentless' };

export function encodeChallenge(c: Challenge): string {
  const base = `${c.seed}.${CODE[c.tier]}`;
  return c.ms !== null && c.steps !== null ? `${base}.${Math.round(c.ms / 100)}.${c.steps}` : base;
}

export function decodeChallenge(code: string | null): Challenge | null {
  if (!code) return null;
  const m = /^(\d{1,10})\.([ntr])(?:\.(\d{1,6})\.(\d{1,5}))?$/.exec(code.trim());
  if (!m) return null;
  const seed = Number(m[1]);
  if (!Number.isSafeInteger(seed) || seed > 4_294_967_295) return null;
  return {
    seed,
    tier: TIER[m[2]!]!,
    ms: m[3] ? Number(m[3]) * 100 : null,
    steps: m[4] ? Number(m[4]) : null,
  };
}

/** The challenge in the page's own address, if any. */
export function challengeFromLocation(): Challenge | null {
  try {
    return decodeChallenge(new URLSearchParams(location.search).get('c'));
  } catch {
    return null;
  }
}

export function challengeUrl(c: Challenge): string {
  return `${location.origin}/?c=${encodeChallenge(c)}`;
}

/** "1:14.2": challenge times are compared to the tenth. */
export function formatChallengeTime(ms: number): string {
  const tenths = Math.round(ms / 100);
  const m = Math.floor(tenths / 600);
  const s = Math.floor((tenths % 600) / 10);
  return `${m}:${String(s).padStart(2, '0')}.${tenths % 10}`;
}
